import { ValidationErrors } from 'final-form';
import { observer } from 'mobx-react';
import React, { FunctionComponent, ReactNode } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  CreateClientUserTask,
  createClientUserTaskFactory,
} from '~admin/interactor/client/perform-create-client-user';
import { ClientProfileData } from '~admin/model/client-profile-data';
import { ClientUserFields } from '~admin/view/components/clients/client-user-fields.component';
import { getClientUserCreateRequest } from '~admin/view/components/clients/helper/get-create-user-request.helper';
import { ClientDetails } from '~admin/view/components/details/client-details/client-details.component';
import { ClientDetailsFormData } from '~admin/view/components/details/client-details/client-dropdowns/client-name-dropdown.component';
import { SuccessMessage } from '~admin/view/components/ui/success-message.component';
import { Box } from '~common/components/box.component';
import { FormButton } from '~common/components/button/form-button.component';
import { Column } from '~common/components/column.component';
import { DataError } from '~common/components/core/data-error.component';
import { EmailValidator } from '~common/components/form/email-field.component';
import { FormFields } from '~common/components/form/form-fields.component';
import { Span } from '~common/components/span.component';
import { Title } from '~common/components/title.component';
import { useTask } from '~common/hook/use-task';
import { validate } from '~common/validator';
import { required } from '~common/validator/validations';

export interface CreateClientUserFormData extends ClientDetailsFormData {
  firstName: string;
  lastName: string;
  email: string;
}

interface ClientFormProps extends FormRenderProps<CreateClientUserFormData> {
  task: CreateClientUserTask;
}

const ClientForm: FunctionComponent<ClientFormProps> = observer(
  ({ task, handleSubmit, invalid, form }) => {
    const { t } = useTranslation();
    const { result, error } = task;

    const onClientCreated = (client: ClientProfileData): void => {
      form.change('clientId', client.id);
    };

    return (
      <FormFields onSubmit={handleSubmit} autoComplete={'off'}>
        <Subtitle>{t('manageClients.select.title')}</Subtitle>
        <StyledClientDetails onClientCreated={onClientCreated} />
        <ClientUserFields />
        <ButtonContainer>
          <FormButton disabled={invalid}>{t('createEvent.createButton')}</FormButton>
        </ButtonContainer>
        {error && <DataError error={error} onClose={(): void => task.reset()} />}
        {result && (
          <SuccessMessage
            successText={t('manageClients.createUser.successMessage')}
            onClose={(): void => {
              task.reset();
              form.reset();
            }}
          />
        )}
      </FormFields>
    );
  },
);

export const Clients: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const createClientUser = useTask(createClientUserTaskFactory);

  const handleSubmit = (values: CreateClientUserFormData): void => {
    createClientUser(getClientUserCreateRequest(values));
  };

  return (
    <Container>
      <StyledTitle>{t('manageClients.headerText')}</StyledTitle>
      <Form
        onSubmit={handleSubmit}
        render={(renderProps): ReactNode => <ClientForm task={createClientUser} {...renderProps} />}
        validate={(values: CreateClientUserFormData): ValidationErrors | undefined => ({
          clientId: validate(values.clientId, [required()]),
          firstName: validate(values.firstName, [required()]),
          lastName: validate(values.lastName, [required()]),
          ...EmailValidator(values),
        })}
      />
    </Container>
  );
};

const Container = styled(Column)`
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  padding: 48px 35px 0;
  max-width: 950px;
`;

const StyledTitle = styled(Title)`
  padding-bottom: 40px;
`;

const Subtitle = styled(Span)`
  font-weight: bold;
  font-size: 22px;
  line-height: 22px;
  margin-bottom: 12px;
`;

const StyledClientDetails = styled(ClientDetails)`
  margin-top: 12px;
`;

const ButtonContainer = styled(Box)`
  margin-top: 16px;
  width: 100%;
  justify-content: flex-end;
`;



// WEBPACK FOOTER //
// ./view/components/clients/clients.component.tsx